import React from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Alert, Box, Chip, List, ListItem, ListItemText, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import SalonPublicStyleScope from '../../salonPublicAppearance/SalonPublicStyleScope';


const HOURS_VARIANTS = new Set(['card', 'table', 'compact', 'week_strip', 'closures']);
const DAY_LABEL_KEYS = Object.freeze({
  lundi: 'days.monday',
  mardi: 'days.tuesday',
  mercredi: 'days.wednesday',
  jeudi: 'days.thursday',
  vendredi: 'days.friday',
  samedi: 'days.saturday',
  dimanche: 'days.sunday',
});

const SalonTemplateOpeningHours = ({
  data,
  variant = 'card',
  visible = true,
  includeClosures = true,
  markClosuresAsBlock = true,
}) => {
  const { t, i18n } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const hoursVariant = HOURS_VARIANTS.has(variant) ? variant : 'card';
  const hours = Array.isArray(data?.hours) ? data.hours : [];
  const closures = Array.isArray(data?.closures) ? data.closures : [];
  const showHours = hoursVariant !== 'closures' && hours.length > 0;
  const showClosures = includeClosures && closures.length > 0;
  const isWeekStrip = hoursVariant === 'week_strip';

  if (!visible || (!showHours && !showClosures)) return null;

  const todayIndex = (() => {
    const day = new Date().getDay();
    return day === 0 ? 6 : day - 1;
  })();

  const closureLabel = (closure) => {
    if (closure.reason) return closure.reason;
    if (closure.isRecurring) {
      return t('landing_salon_details.closure_recurring', {
        day: String(closure.day ?? '').padStart(2, '0'),
        month: String(closure.month ?? '').padStart(2, '0'),
      });
    }
    if (closure.startAt) {
      return new Date(closure.startAt).toLocaleDateString(i18n.language || 'fr-FR');
    }
    return t('landing_salon_details.closure_default');
  };

  return (
    <>
      {showClosures ? (
        <Alert
          severity="warning"
          data-salon-template-block={markClosuresAsBlock ? 'closures' : undefined}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography component="h2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('landing_salon_details.closures_title')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {closures.slice(0, 6).map((closure) => (
              <Chip
                key={closure.id ?? `${closure.startAt}-${closure.endAt}-${closure.month}-${closure.day}`}
                label={closureLabel(closure)}
                sx={{ bgcolor: alpha(theme.colors.warning[600], 0.12), fontWeight: 600 }}
              />
            ))}
          </Box>
        </Alert>
      ) : null}

      {showHours ? (
        <SalonPublicStyleScope
          sectionKey="opening_hours"
          data-testid="salon-section-opening_hours"
          data-salon-template-block="opening-hours"
          data-variant={hoursVariant}
          sx={{
            backgroundColor: theme.colors.background.primary,
            borderRadius: isWeekStrip ? 3 : 4,
            border: `1px solid ${theme.colors.border.light}`,
            p: isWeekStrip ? { xs: 1.5, md: 2 } : 3,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isWeekStrip ? 1 : 2 }}>
            <AccessTimeIcon sx={{ fontSize: '1.2rem', color: theme.colors.primary[600] }} />
            <Typography component="h2" data-salon-text-role="title" sx={{ fontWeight: 700, color: theme.colors.gray[700] }}>
              {t('landing_salon_details.opening_hours')}
            </Typography>
          </Box>
          <List
            data-testid="salon-opening-hours-list"
            data-layout={isWeekStrip ? 'horizontal' : 'vertical'}
            data-salon-text-role="content"
            dense
            disablePadding
            sx={isWeekStrip ? {
              display: 'grid',
              gridTemplateColumns: {
                xs: `repeat(${hours.length}, minmax(132px, 1fr))`,
                lg: `repeat(${hours.length}, minmax(0, 1fr))`,
              },
              gap: 1,
              overflowX: { xs: 'auto', lg: 'visible' },
              pb: { xs: 0.75, lg: 0 },
              scrollSnapType: { xs: 'x proximity', lg: 'none' },
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { height: 6 },
            } : undefined}
          >
            {hours.map((entry, index) => (
              <ListItem
                key={entry.day}
                disableGutters
                sx={{
                  py: isWeekStrip ? 0.8 : 0.5,
                  px: isWeekStrip ? 1.25 : 0,
                  borderRadius: 2,
                  bgcolor: index === todayIndex ? alpha(theme.salon.primary, 0.08) : 'transparent',
                  border: isWeekStrip
                    ? `1px solid ${index === todayIndex
                      ? alpha(theme.salon.primary, 0.38)
                      : theme.colors.border.light}`
                    : 'none',
                  minWidth: 0,
                  scrollSnapAlign: isWeekStrip ? 'start' : 'none',
                }}
              >
                <ListItemText
                  sx={{ m: 0 }}
                  primary={(
                    <Box sx={{
                      display: 'flex',
                      flexDirection: isWeekStrip ? 'column' : 'row',
                      alignItems: isWeekStrip ? 'center' : 'initial',
                      justifyContent: isWeekStrip ? 'center' : 'space-between',
                      gap: isWeekStrip ? 0.5 : 2,
                      minWidth: 0,
                      textAlign: isWeekStrip ? 'center' : 'initial',
                    }}>
                      <Typography
                        noWrap={isWeekStrip}
                        sx={{
                          width: isWeekStrip ? '100%' : 'auto',
                          fontWeight: index === todayIndex ? 700 : 600,
                          color: theme.colors.gray[700],
                        }}
                      >
                        {t(DAY_LABEL_KEYS[entry.day] || entry.day)}
                      </Typography>
                      <Typography noWrap sx={{ color: theme.colors.gray[600] }}>
                        {entry.isClosed
                          ? t('landing_salon_details.closed')
                          : `${entry.open} - ${entry.close}`}
                      </Typography>
                    </Box>
                  )}
                />
              </ListItem>
            ))}
          </List>
        </SalonPublicStyleScope>
      ) : null}
    </>
  );
};

export default SalonTemplateOpeningHours;
